import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api'
  : '/api';

/**
 * Kaldıraç Grupları — Admin panel bileşeni.
 * Backend: GET/POST /api/admin/leverage-groups
 * Spec Bölüm 2 & 11: tek kaynak, ürünler bu tabloya bağlanır.
 * Not: `hisse` grubu L=1 zorunlu (backend zorluyor).
 */
export default function LeverageGroups() {
  const [groups, setGroups] = useState({});
  const [labels, setLabels] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const cfg = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/leverage-groups`, cfg());
      setGroups(res.data.groups || {});
      setLabels(res.data.labels || {});
      setUpdatedAt(res.data.updated_at || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setValue = (key, val) => {
    setGroups((g) => ({ ...g, [key]: Math.max(1, parseInt(val || 1, 10)) }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setMsg('');
    try {
      const res = await axios.post(`${API}/admin/leverage-groups`, { groups }, cfg());
      setGroups(res.data.groups || groups);
      setMsg('Kaydedildi. Yeni pozisyonlar bu değerlere göre açılır.');
      setUpdatedAt(new Date().toISOString());
      setTimeout(() => setMsg(''), 3500);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-400 p-6" data-testid="leverage-groups-loading">Yükleniyor…</div>;
  }

  const orderedKeys = ['hisse', 'forex', 'emtia', 'exchange', 'endeks', 'abd_hisse', 'viop'];

  return (
    <div className="bg-gray-900 rounded-xl border border-gray-700 p-6" data-testid="leverage-groups-panel">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-1">Kaldıraç Grupları</h2>
        <p className="text-gray-400 text-sm">
          Ürünler bir gruba bağlıdır. Kaldıraç yalnızca bu tablodan okunur.
          Değişiklik <span className="text-yellow-400 font-semibold">yalnızca yeni açılan</span> pozisyonları etkiler;
          açık pozisyonlar açılıştaki kaldıraçlarını korur.
        </p>
        {updatedAt && (
          <p className="text-xs text-gray-500 mt-2">Son güncelleme: {new Date(updatedAt).toLocaleString('tr-TR')}</p>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-900/40 border border-red-700 text-red-300 text-sm" data-testid="leverage-groups-error">
          {error}
        </div>
      )}
      {msg && (
        <div className="mb-4 p-3 rounded bg-green-900/40 border border-green-700 text-green-300 text-sm" data-testid="leverage-groups-success">
          {msg}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {orderedKeys.filter((k) => k in groups).map((k) => (
          <div key={k} className="bg-gray-800 rounded-lg p-4 border border-gray-700" data-testid={`leverage-group-${k}`}>
            <label className="block text-sm font-semibold text-gray-300 mb-1">
              {labels[k] || k}
              <span className="ml-2 text-xs text-gray-500 font-mono">({k})</span>
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={1}
                max={500}
                value={groups[k]}
                disabled={k === 'hisse'}
                onChange={(e) => setValue(k, e.target.value)}
                className={`flex-1 bg-gray-900 text-white font-mono px-3 py-2 rounded border ${k === 'hisse' ? 'border-gray-700 opacity-60' : 'border-gray-600 focus:border-blue-500 focus:outline-none'}`}
                data-testid={`leverage-group-input-${k}`}
              />
              <span className="text-lg text-white font-bold">x</span>
            </div>
            {k === 'hisse' && (
              <p className="text-xs text-gray-500 mt-2">
                Hisse grubu L=1 zorunlu (spot davranış temelidir, değiştirilemez).
              </p>
            )}
            {k !== 'hisse' && (
              <p className="text-xs text-gray-500 mt-2">
                %{(100 / groups[k]).toFixed(3)} ters hareket teminatı sıfırlar.
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-6 py-2 rounded bg-blue-600 hover:bg-blue-500 text-white font-semibold disabled:opacity-60"
          data-testid="leverage-groups-save-btn"
        >
          {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
        <button
          onClick={load}
          disabled={saving}
          className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm"
          data-testid="leverage-groups-reload-btn"
        >
          Yenile
        </button>
      </div>

      <div className="mt-8 text-xs text-gray-500 leading-relaxed">
        <p>
          📚 <span className="text-gray-400 font-semibold">Nasıl çalışır?</span>
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Kullanıcı ₺{'X'} yatırım tutarı girer. Sistem <code className="text-blue-400">adet = X × L / fiyat</code> hesaplar.</li>
          <li>Kâr/Zarar <span className="text-yellow-400 font-semibold">L ile ÇARPILMAZ</span> — L etkisi adet içinde.</li>
          <li>%1 ters hareket L=100&apos;de teminatın tamamını silme demektir (1:100 tanımı).</li>
          <li>Değişikliklerden sonra <span className="text-yellow-400">açık pozisyonlar etkilenmez</span>, yalnızca yeni açılışlar.</li>
        </ul>
      </div>
    </div>
  );
}
